import React from "react";
import { MessageSquareReply, ShieldBan, X } from "lucide-react";
import { Button } from "ui/Button";
import ActorAvatar from "components/Actors/ActorAvatar";
import ActorName from "components/Actors/ActorName";
import { ACTOR_AVATAR_SIZES } from "util/actorAvatarSizes";
import { toActorFromChatActor } from "util/actorIdentity";
import type { ChatPendingRequest } from "types/chat";
import { useChatRequestActionMutation } from "hooks/queries/useChatQueries";
import { getConversationTitle } from "./utils";

interface ChatRequestProps {
  request: ChatPendingRequest;
  onOpenConversation?: (conversationPublicId: string) => void;
}

const ChatRequest: React.FC<ChatRequestProps> = ({ request, onOpenConversation }) => {
  const acceptMutation = useChatRequestActionMutation("accept");
  const declineMutation = useChatRequestActionMutation("decline");
  const blockMutation = useChatRequestActionMutation("block");

  const conversationPublicId = request.conversation?.public_id || "";
  const requesterActor = toActorFromChatActor(request.requester);
  const requesterDisplayName =
    request.requester.display_name || request.requester.username;

  return (
    <div className="rounded-xl border border-eoe-border-main dark:border-eoe-dark-border-main bg-transparent p-4">
      <div className="flex items-start gap-3">
        <ActorAvatar actor={requesterActor} size={ACTOR_AVATAR_SIZES.header} />
        <div className="min-w-0 flex-1">
          {requesterActor ? (
            <ActorName
              actor={requesterActor}
              label={requesterDisplayName}
              className="font-medium text-eoe-text dark:text-eoe-dark-text"
            />
          ) : (
            <div className="font-medium text-eoe-text dark:text-eoe-dark-text">
              {requesterDisplayName}
            </div>
          )}
          <div className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {request.requester.username}
          </div>
          <div className="mt-2 text-sm text-eoe-text dark:text-eoe-dark-text">
            {request.conversation ? getConversationTitle(request.conversation, "Conversation request") : "Conversation request"}
          </div>
          {conversationPublicId ? (
            <button
              type="button"
              className="mt-2 text-xs text-soc-purple hover:underline"
              onClick={() => onOpenConversation?.(conversationPublicId)}
            >
              Preview conversation
            </button>
          ) : null}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          onClick={() => acceptMutation.mutate(request.id)}
          disabled={acceptMutation.isPending || declineMutation.isPending || blockMutation.isPending}
        >
          <MessageSquareReply />
          Accept
        </Button>
        <Button
          variant="outline"
          onClick={() => declineMutation.mutate(request.id)}
          disabled={acceptMutation.isPending || declineMutation.isPending || blockMutation.isPending}
        >
          <X />
          Decline
        </Button>
        <Button
          variant="outline"
          onClick={() => blockMutation.mutate(request.id)}
          disabled={acceptMutation.isPending || declineMutation.isPending || blockMutation.isPending}
        >
          <ShieldBan />
          Block
        </Button>
      </div>
    </div>
  );
};

export default ChatRequest;
